import { useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { History, Search } from "lucide-react";
import { gbp, priceDiff, safeHttpsUrl, signedGbp, signedPct, type HistoryEntry, type ReviewOverview } from "@/lib/price-review";

const TYPE_LABELS: Record<string, string> = { materials: "Material", labour: "Labour", plant: "Plant", "work-item": "Work item" };

export function PriceReviewHistory({ overview, onOpen }: { overview: ReviewOverview; onOpen: (tab: "materials" | "labour") => void }) {
  const history = useMemo<HistoryEntry[]>(() => [...(overview.history ?? [])].sort((a, b) => b.appliedAt.localeCompare(a.appliedAt)), [overview]);
  const s = overview.summary ?? { pending: 0, approved: 0, completed: history.length, rejected: 0, deferred: 0 };
  const [q, setQ] = useState(""); const [type, setType] = useState("all");
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return history.filter((h) => (type === "all" || h.type === type)
      && (!t || `${h.taskName} ${h.taskId} ${h.itemName} ${h.field} ${h.batchName ?? ""} ${h.source ?? ""} ${h.actor ?? ""}`.toLowerCase().includes(t)));
  }, [history, q, type]);
  const stats: [string, number, string][] = [
    ["Pending review", s.pending, "pending"], ["Approved, not applied", s.approved, "approved"], ["Completed", s.completed, "completed"],
    ["Rejected", s.rejected, "rejected"], ["Deferred", s.deferred, "deferred"],
  ];
  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="stats-review-summary">
        {stats.map(([l, v, k]) => (
          <div key={k} className={`rounded-md border p-3 ${k === "completed" ? "border-primary/50 bg-primary/5" : ""}`}>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{l}</div>
            <div className="text-xl font-semibold tabular-nums" data-testid={`stat-${k}`}>{v}</div>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">Completed means a committed development Cost Library update. Approval alone is not a change. Values below are the before and after snapshots recorded at the time, not today's library.</p>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><History className="h-4 w-4" /> Completed changes</CardTitle>
          <CardDescription>{filtered.length} of {history.length} recorded updates</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search work item, material, batch, actor" className="h-8 pl-8 text-sm" data-testid="input-search-history" />
            </div>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="w-full sm:w-40 h-8 text-xs" data-testid="select-filter-history-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">All types</SelectItem>
                {Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k} className="text-xs">{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {history.length === 0 ? (
            <div className="py-10 text-center space-y-3" data-testid="empty-history">
              <p className="text-sm text-muted-foreground max-w-md mx-auto">No completed Cost Library changes recorded yet. Changes appear here once approved research is applied.</p>
              <div className="flex justify-center gap-2">
                <Button size="sm" variant="outline" onClick={() => onOpen("materials")} data-testid="button-goto-materials">Review materials</Button>
                <Button size="sm" variant="outline" onClick={() => onOpen("labour")} data-testid="button-goto-labour">Review labour</Button>
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-10 text-center space-y-2" data-testid="empty-filtered-history">
              <p className="text-sm text-muted-foreground">No changes match.</p>
              <Button size="sm" variant="outline" onClick={() => { setQ(""); setType("all"); }} data-testid="button-reset-history-filters">Reset filters</Button>
            </div>
          ) : (
            <div className="overflow-x-auto max-h-[65vh] overflow-y-auto">
              <Table className="text-xs">
                <TableHeader><TableRow>
                  <TableHead>Work item / item</TableHead><TableHead>Type</TableHead>
                  <TableHead className="text-right">Previous</TableHead><TableHead className="text-right">Updated</TableHead><TableHead className="text-right">Difference</TableHead>
                  <TableHead>Applied</TableHead><TableHead>Source</TableHead><TableHead>Status</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {filtered.map((h) => {
                    const isQuantity = /quantity factor|waste/i.test(h.field);
                    const unitChanged = (h.previousUnit ?? "") !== (h.updatedUnit ?? "");
                    const d = unitChanged ? null : priceDiff(h.previousPrice, h.updatedPrice);
                    const amount = (value: number | null) => isQuantity ? value == null ? "—" : value.toLocaleString("en-GB") : gbp(value);
                    const sourceUrl = safeHttpsUrl(h.source);
                    return (
                      <TableRow key={h.id} data-testid={`row-history-${h.id}`}>
                        <TableCell className="max-w-[260px]">
                          <p className="font-medium text-sm truncate" title={h.taskName}>{h.taskName}</p>
                          <p className="text-muted-foreground truncate">{h.itemName}{h.field ? ` · ${h.field}` : ""}</p>
                        </TableCell>
                        <TableCell><Badge variant="outline" className="text-[10px]">{TYPE_LABELS[h.type] ?? h.type}</Badge></TableCell>
                        <TableCell className="text-right tabular-nums whitespace-nowrap text-muted-foreground">{amount(h.previousPrice)}{h.previousUnit ? ` / ${h.previousUnit}` : ""}</TableCell>
                        <TableCell className="text-right tabular-nums whitespace-nowrap font-medium">{amount(h.updatedPrice)}{h.updatedUnit ? ` / ${h.updatedUnit}` : ""}
                          {unitChanged && <div className="text-[10px] text-amber-700 dark:text-amber-400">unit changed</div>}</TableCell>
                        <TableCell className={`text-right tabular-nums whitespace-nowrap ${d && d.abs > 0 ? "text-rose-700 dark:text-rose-400" : d && d.abs < 0 ? "text-emerald-700 dark:text-emerald-400" : "text-muted-foreground"}`} data-testid={`text-history-diff-${h.id}`}>
                          {d ? <>{isQuantity ? `${d.abs > 0 ? "+" : ""}${d.abs.toLocaleString("en-GB")}` : signedGbp(d.abs)}<div className="text-[10px]">{signedPct(d.pct)}</div></> : unitChanged ? "Different unit basis" : "—"}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">{new Date(h.appliedAt).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}<div className="text-muted-foreground">{h.actor || "actor not recorded"}</div></TableCell>
                        <TableCell className="max-w-[180px] text-muted-foreground"><div className="truncate" title={h.batchName ?? h.source ?? ""}>{h.batchName || h.source || "Manual edit"}</div>
                          {sourceUrl && <a className="underline" href={sourceUrl} target="_blank" rel="noopener noreferrer">Research source</a>}
                        </TableCell>
                        <TableCell><Badge className="text-[10px] no-default-hover-elevate bg-emerald-600 text-white" data-testid={`status-history-${h.id}`}>{h.status}</Badge>
                          <div className="text-[10px] text-muted-foreground mt-0.5">{h.environment === "development" ? "Development" : h.environment}</div></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
